import { useEffect, useState, useRef } from 'react';
import { BordOneToneIcon } from 'components/NudosComponents';
import NudosConfirmActionModal from 'components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { nudosGray500 } from '../../../../../styles/styles.constants';
import ProductInformation from '../ProductInformation/ProductInformation';
import { IToolData } from 'types/products';
import GeneralTab from './GeneralTab/GeneralTab';
import { ICountryDetail } from 'types/countries';
import { QRDownloadButton } from 'components';
import ProductEditConditional from './ProductEditConditional/ProductEditConditional';
import ProductEditState from './ProductEditState/ProductEditState';
import ProductEditSkuAndSerial from './ProductEditSkuAndSerial/ProductEditSkuAndSerial';
import { LOCATION_PLACE_TYPE } from 'types/services';
import { getProductById } from 'services/toolsModule';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import ToolInformationTab from './ToolInformationTab/ToolInformationTab';
import { ToolHistoryTab } from './ToolHistoryTab/ToolHistoryTab';
import useLockScroll from 'hooks/useLockScroll';

import './ProductDetailModalContent.scss';

const ProductDetailModalContent = ({
  onClose,
  productIdSelected,
  listOfCountries,
  refreshToolsListing
}: IProductDetailModalContentProps) => {
  const mainTitleModalDefault = 'Interna de la herramienta';
  const [activeTab, setActiveTab] = useState<number>(0);
  const [mainTitleModal, setMainTitleModal] = useState<string>(mainTitleModalDefault);
  const [showInfoToolTab, setShowInfoToolTab] = useState<boolean>(true);
  const [showConditionalEdition, setShowConditionalEdition] = useState<boolean>(false);
  const [showStateEdition, setShowStateEdition] = useState<boolean>(false);
  const [showSkuAndSerialEdition, setShowSkuAndSerialEdition] = useState<boolean>(false);
  const [productData, setProductData] = useState<IToolData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshProductData, setRefreshProductData] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<(() => void) | null>(null);
  const conditionalRef = useRef<{ hasUnsavedChanges: () => boolean }>(null);
  const stateRef = useRef<{ hasUnsavedChanges: () => boolean }>(null);
  useLockScroll(true);
  const showArrowBack = showConditionalEdition || showStateEdition || showSkuAndSerialEdition;
  const handleShowConditionalEdition = () => {
    setMainTitleModal('Modificar la condición de la herramienta');
    setShowConditionalEdition(!showConditionalEdition);
    setShowInfoToolTab(false);
  };

  const handleShowStateEdition = () => {
    setMainTitleModal('Modificar el estado de la herramienta');
    setShowStateEdition(!showStateEdition);
    setShowInfoToolTab(false);
  };

  const handleShowSkuAndSerialEdition = () => {
    setMainTitleModal('Modificar datos de la herramienta');
    setShowSkuAndSerialEdition(!showSkuAndSerialEdition);
    setShowInfoToolTab(false);
  };

  const checkUnsavedChanges = (): boolean => {
    const checks = [
      { show: showConditionalEdition, ref: conditionalRef },
      { show: showStateEdition, ref: stateRef }
    ];

    return checks.some(({ show, ref }) => show && ref.current?.hasUnsavedChanges());
  };

  const handleCloseWithValidation = (closeAction: () => void) => {
    if (!checkUnsavedChanges()) return closeAction();
    setPendingCloseAction(() => closeAction);
    setShowConfirmationModal(true);
  };

  const handleExitWithoutSaving = () => {
    setShowConfirmationModal(false);
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  const handleContinueEditing = () => {
    setShowConfirmationModal(false);
    setPendingCloseAction(null);
  };

  const closeEditionModals = () => {
    setShowConditionalEdition(false);
    setShowStateEdition(false);
    setShowSkuAndSerialEdition(false);
    setMainTitleModal(mainTitleModalDefault);
    setShowInfoToolTab(true);
  };

  const goBackArrowBack = (skipValidation: boolean = false) => {
    const hasAnyModalOpen = showConditionalEdition || showStateEdition || showSkuAndSerialEdition;
    if (!hasAnyModalOpen) return;

    skipValidation ? closeEditionModals() : handleCloseWithValidation(closeEditionModals);
  };

  const handleArrowBackClick = () => {
    goBackArrowBack();
  };

  const handleCloseModal = () => {
    const hasAnyModalOpen = showConditionalEdition || showStateEdition || showSkuAndSerialEdition;
    hasAnyModalOpen ? handleCloseWithValidation(onClose) : onClose();
  };

  const getProductData = async () => {
    setIsLoading(true);
    try {
      const productData = await getProductById(productIdSelected.toString());
      setProductData(productData);
      setRefreshProductData(false);
    } catch (error) {
      displayErrorNotification();
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductDataRefresh = () => {
    if (refreshProductData || productIdSelected) {
      getProductData();
    }
  };

  useEffect(() => {
    handleProductDataRefresh();
  }, [productIdSelected, refreshProductData]);

  const contentTabs = [
    {
      title: 'General',
      onClick: () => setActiveTab(0),
      component: (
        <GeneralTab
          productData={productData ?? null}
          listOfCountries={listOfCountries}
          handleShowConditionalEdition={handleShowConditionalEdition}
          handleShowStateEdition={handleShowStateEdition}
          handleShowSkuAndSerialEdition={handleShowSkuAndSerialEdition}
          isLoading={isLoading}
        />
      )
    },
    {
      title: 'Info. de la herramienta',
      onClick: () => setActiveTab(1),
      component: <ToolInformationTab productId={productIdSelected} />
    },
    {
      title: 'Historial',
      onClick: () => setActiveTab(2),
      component: <ToolHistoryTab productId={productIdSelected} />
    }
  ];

  const qrData = {
    productId: productIdSelected || productData?.id,
    serial: productData?.serial,
    orgName: productData?.organization?.organizationName
  };

  const disableQrDownload = ![
    LOCATION_PLACE_TYPE.NUDOS,
    LOCATION_PLACE_TYPE.DIAGNOSTIC,
    LOCATION_PLACE_TYPE.WARRANTY
  ].includes(productData?.location?.place as LOCATION_PLACE_TYPE);

  if (!productData) return null;
  return (
    <div className="productDetailModalContent">
      <div className="header">
        {showArrowBack && (
          <div className="returnArrow" onClick={handleArrowBackClick}>
            <BordOneToneIcon variant="arrowLeft" customWidth="16px" stroke={nudosGray500} />
          </div>
        )}
        <p className="title">{mainTitleModal}</p>
        <div className="exitContentButton" onClick={handleCloseModal}>
          <BordOneToneIcon variant="x" customWidth="16px" stroke={nudosGray500} />
        </div>
      </div>
      <div className="content">
        <ProductInformation productData={productData} />
        {showInfoToolTab && (
          <div className="contentTabs">
            <div className="headerTabs">
              <div className="containerTabs">
                {contentTabs?.length > 0 &&
                  contentTabs?.map(tab => (
                    <div
                      className={`tab ${activeTab === contentTabs.indexOf(tab) ? 'active' : ''}`}
                      key={tab.title}
                      onClick={tab.onClick}
                    >
                      <p className="tabTitle">{tab.title}</p>
                    </div>
                  ))}
              </div>
              <QRDownloadButton QRData={qrData} isModeDark={true} disabled={disableQrDownload} />
            </div>
            <div className="containerContent">{contentTabs[activeTab].component}</div>
          </div>
        )}
        {showConditionalEdition && (
          <ProductEditConditional
            ref={conditionalRef}
            productData={productData}
            onClose={goBackArrowBack}
            setRefreshProductData={setRefreshProductData}
            refreshToolsListing={refreshToolsListing}
          />
        )}
        {showStateEdition && (
          <ProductEditState
            ref={stateRef}
            productData={productData}
            onClose={goBackArrowBack}
            setRefreshProductData={setRefreshProductData}
            refreshToolsListing={refreshToolsListing}
          />
        )}
        {showSkuAndSerialEdition && (
          <ProductEditSkuAndSerial
            productData={productData}
            onClose={goBackArrowBack}
            refreshToolsListing={refreshToolsListing}
            setRefreshProductData={setRefreshProductData}
          />
        )}
      </div>
      {showConfirmationModal && (
        <NudosConfirmActionModal
          modalTitle="Tienes cambios sin guardar"
          modalText={
            showStateEdition
              ? 'Si sales, se perderá la información ingresada. ¿Seguro que quieres cancelar modificación del estado?'
              : 'Si sales, se perderá la información ingresada. ¿Seguro que quieres cancelar modificación de la condición?'
          }
          firstButtonText="Salir sin guardar"
          secondButtonText="Seguir editando"
          firstButtonAction={handleExitWithoutSaving}
          secondButtonAction={handleContinueEditing}
          customHeight="260px"
          customStyleModalTitle={{ marginBottom: '5px' }}
        />
      )}
    </div>
  );
};
interface IProductDetailModalContentProps {
  onClose: () => void;
  productIdSelected: number;
  listOfCountries: ICountryDetail[];
  refreshToolsListing: () => void;
}
export default ProductDetailModalContent;
